/* eslint-disable no-console -- This IS the frontend logger; it must use console as the output transport */
import pino from 'pino';
import { FRONTEND_LOG_CONTEXT_FIELDS, FRONTEND_LOG_MAX_FIELD_LEN } from '@shared/frontend-logs';

const level = String(import.meta.env?.VITE_LOG_LEVEL || 'info').toLowerCase();
const isDev = import.meta.env?.DEV;

/**
 * Endpoint that receives frontend warn+ log batches.
 *
 * In dev: /__dev/errors  (legacy alias, unauthenticated dev-only endpoint)
 * In prod/staging: /api/frontend-logs  (auth required; rate-limited + payload-capped server-side)
 *
 * Same-origin fetch with `keepalive: true` sends the session cookie automatically —
 * no explicit `credentials` option needed. The server enforces a valid Clerk session.
 * See src/api/frontend-logs.ts for the full security model.
 */
const LOG_COLLECT_URL = isDev ? '/__dev/errors' : '/api/frontend-logs';

type LogEntry = Record<string, unknown>;
type TransmitLogEvent = pino.LogEvent;

const SHORT_STRING_FIELDS = ['category', 'component', 'pathname', 'release', 'error'] as const;
const STACK_FIELDS = ['stack', 'componentStack'] as const;
function clamp(s: string): string {
  return s.length > FRONTEND_LOG_MAX_FIELD_LEN ? `${s.slice(0, FRONTEND_LOG_MAX_FIELD_LEN)}...` : s;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isPrimitiveArg(v: unknown): v is string | number | boolean | bigint | null | undefined {
  return v === null || v === undefined || typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint';
}

function readError(v: unknown): { error?: string; stack?: string } {
  if (v instanceof Error) return { error: clamp(v.message || v.name), ...(typeof v.stack === 'string' ? { stack: clamp(v.stack) } : {}) };
  if (isRecord(v) && typeof v.message === 'string') {
    return { error: clamp(v.message), ...(typeof v.stack === 'string' ? { stack: clamp(v.stack) } : {}) };
  }
  return {};
}

/**
 * Flatten one pino `logEvent` into the single bounded entry the collector
 * expects. pino's browser transmit hands over `messages` (every call arg) and
 * `bindings` (child-logger fields); posting them as separate entries lost the
 * issues/msg pairing and the `category` binding. Only known diagnostic fields
 * are forwarded, each clamped (`issues` arrive already summarized; the
 * collector validates every item). Never throws.
 */
export function toLogEntry(logEvent: TransmitLogEvent): LogEntry {
  const entry: LogEntry = {};
  try {
    const positional: Array<string | number | boolean | bigint | null | undefined> = [];
    let objectMsg: string | undefined;

    const absorb = (arg: unknown, isBinding: boolean): void => {
      if (arg instanceof Error) {
        Object.assign(entry, readError(arg));
        return;
      }
      if (!isRecord(arg)) {
        if (!isBinding && isPrimitiveArg(arg)) positional.push(arg);
        return;
      }
      for (const key of SHORT_STRING_FIELDS) {
        if (typeof arg[key] === 'string') entry[key] = clamp(arg[key]);
      }
      for (const key of STACK_FIELDS) {
        if (typeof arg[key] === 'string') entry[key] = clamp(arg[key]);
      }
      for (const key of FRONTEND_LOG_CONTEXT_FIELDS) {
        if (typeof arg[key] === 'string' && arg[key] !== '') entry[key] = clamp(arg[key]);
      }
      if (typeof arg.issueCount === 'number') entry.issueCount = arg.issueCount;
      // A `msg` field is a fallback message, kept apart from the call's own message args.
      if (typeof arg.msg === 'string') objectMsg = arg.msg;
      if ('err' in arg) Object.assign(entry, readError(arg.err));
      if (Array.isArray(arg.issues)) entry.issues = arg.issues;
    };

    for (const b of logEvent.bindings ?? []) absorb(b, true);
    for (const m of logEvent.messages ?? []) absorb(m, false);

    if (positional.length > 0) entry.msg = clamp(positional.map(String).join(' '));
    else if (objectMsg !== undefined) entry.msg = clamp(objectMsg);
    return entry;
  } catch {
    // A log line must never throw into the caller.
    return { msg: 'log entry unavailable' };
  }
}

function sendLogEvent(msgLevel: string, logEvent: TransmitLogEvent): void {
  fetch(LOG_COLLECT_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ level: msgLevel, messages: [toLogEntry(logEvent)] }),
    keepalive: true,
  }).catch(() => {});  // fire-and-forget
}

/** Exported so a test can build the logger from pino's BROWSER entry (the Node build never runs `transmit`). */
export function createBrowserLogger(factory: typeof pino = pino) {
  return factory({
    level,
    browser: {
      asObject: true,
      write: {
        trace: (o) => console.debug(o),
        debug: (o) => console.debug(o),
        info: (o) => console.info(o),
        warn: (o) => console.warn(o),
        error: (o) => console.error(o),
        fatal: (o) => console.error(o),
      },
      // Ship warn+ to the backend log collector in all environments.
      // This makes validateResponse() warnings observable in production Render logs.
      transmit: { level: 'warn', send: sendLogEvent },
    },
  });
}

export const logger = createBrowserLogger();

// Child loggers for categories
export const authLogger = logger.child({ category: 'auth' });
export const apiLogger = logger.child({ category: 'api' });
